'use client';

import {
  makeAutoObservable,
  runInAction,
} from 'mobx';

import {
  getDefaultSelectionForModel,
  getParameterValue as getPresetParameterValue,
  mergeSelectionWithEditorDefaults,
  sanitiseSelectionParams,
} from './assistantModelPresets';
import {
  ASSISTANT_DEFAULT_PINNED_MODEL_IDS,
  normalisePinnedModelIds,
  resolveRecommendedPinnedIds,
} from './assistantPinnedModels';

import type { AssistantStore } from './assistantStore';
import type {
  GatewayModelInfo,
  GatewayModelParameterDefinition,
  GatewayModelParameterSelection,
  GatewayModelSelection,
  GatewayRunMode,
  GatewaySettings,
} from '@/app/sdk';

import { localStorageHelper } from '@/lib/localStorageHelper';

const STORAGE_KEY_SHOW_REASONING = 'genesys-ai-assistant-show-reasoning';
const STORAGE_KEY_MESSAGE_CONTENT_FONT_SIZE = 'genesys-ai-assistant-message-content-font-size';

const MESSAGE_CONTENT_FONT_SIZE_MIN_PX = 11;
const MESSAGE_CONTENT_FONT_SIZE_MAX_PX = 15;
const MESSAGE_CONTENT_FONT_SIZE_DEFAULT_PX = 13;

const CONTEXT_PARAM_IDS = new Set(['context', 'contextwindow', 'context_window', 'contextWindow']);

const DEBUG = {
  logAvailableModels: false && process.env.NODE_ENV === 'development',
} as const;

function clampMessageContentFontSize(px: number): number {
  return Math.min(MESSAGE_CONTENT_FONT_SIZE_MAX_PX, Math.max(MESSAGE_CONTENT_FONT_SIZE_MIN_PX, px));
}

function getMessageContentFontSizeFromStorage(): number {
  const storedSize = localStorageHelper.getNumber(
    STORAGE_KEY_MESSAGE_CONTENT_FONT_SIZE,
    MESSAGE_CONTENT_FONT_SIZE_DEFAULT_PX,
  );
  return clampMessageContentFontSize(
    storedSize ?? MESSAGE_CONTENT_FONT_SIZE_DEFAULT_PX,
  );
}

function parseContextTokens(label: string): number | undefined {
  if (!label) {
    return undefined;
  }
  const normalised = label.toLowerCase().replace(/\s/g, '');
  const match = normalised.match(/(\d+(?:\.\d+)?)(k|m)?/);
  if (!match?.[1]) {
    return undefined;
  }
  const base = Number.parseFloat(match[1]);
  if (!Number.isFinite(base)) {
    return undefined;
  }
  const unit = match[2];
  if (unit === 'm') {
    return Math.round(base * 1_000_000);
  }
  if (unit === 'k') {
    return Math.round(base * 1_000);
  }
  return Math.round(base);
}

export class AssistantModelStore {
  showModelListDialog = false;
  runMode: GatewayRunMode = 'agent';
  defaultModel: GatewayModelSelection = { id: 'composer-2.5' };
  chatModel: GatewayModelSelection | null = null;
  pinnedModelIds: string[] = [...ASSISTANT_DEFAULT_PINNED_MODEL_IDS];
  availableModels: GatewayModelInfo[] = [];
  isLoadingModels = false;
  showReasoning = localStorageHelper.getBoolean(STORAGE_KEY_SHOW_REASONING, true);
  messageContentFontSizePx = getMessageContentFontSizeFromStorage();

  constructor(private readonly root: AssistantStore) {
    makeAutoObservable(this, {}, { autoBind: true });
  }

  get chatModelId(): string | null {
    return this.chatModel?.id ?? null;
  }

  get effectiveChatModel(): GatewayModelSelection {
    return this.chatModel ?? this.defaultModel;
  }

  get defaultModelInfo(): GatewayModelInfo | undefined {
    return this.findModelById(this.defaultModel.id);
  }

  get chatModelInfo(): GatewayModelInfo | undefined {
    return this.findModelById(this.effectiveChatModel.id);
  }

  get defaultModelVariantName(): string | null {
    return this.getSelectedVariantName(this.defaultModel, this.defaultModelInfo);
  }

  get chatModelVariantName(): string | null {
    return this.getSelectedVariantName(this.effectiveChatModel, this.chatModelInfo);
  }

  get contextWindowTokensEstimate(): number | undefined {
    const model = this.chatModelInfo;
    if (!model) {
      return undefined;
    }

    const fromSelection = this.resolveContextTokensFromSelection(model);
    if (fromSelection !== undefined) {
      return fromSelection;
    }

    const fixed = model.contextWindowTokens;
    if (typeof fixed === 'number' && fixed > 0) {
      return fixed;
    }

    return undefined;
  }

  findAssistantModelById(modelId: string | null | undefined): GatewayModelInfo | undefined {
    return this.findModelById(modelId);
  }

  openModelListDialog(): void {
    this.showModelListDialog = true;
    void this.loadAvailableModels();
  }

  closeModelListDialog(): void {
    this.showModelListDialog = false;
  }

  async setRunMode(runMode: GatewayRunMode): Promise<void> {
    runInAction(() => {
      this.runMode = runMode;
    });
    await this.root.updateGatewaySettings({ runMode });
  }

  async setModel(modelId: string | null): Promise<void> {
    runInAction(() => {
      this.chatModel = modelId ? this.createInitialModelSelection(modelId) : null;
    });
  }

  applyChatModelSelection(selection: GatewayModelSelection | null): void {
    runInAction(() => {
      this.chatModel = selection;
    });
  }

  async setPinnedModelIds(modelIds: string[]): Promise<void> {
    const nextIds = normalisePinnedModelIds(this.availableModels, modelIds);
    runInAction(() => {
      this.pinnedModelIds = nextIds;
    });
    await this.root.updateGatewaySettings({ pinnedModelIds: nextIds });
  }

  async applyRecommendedPinnedModels(): Promise<void> {
    const recommended = resolveRecommendedPinnedIds(this.availableModels);
    if (recommended.length === 0) {
      return;
    }
    await this.setPinnedModelIds(recommended);
  }

  async toggleModelPinned(modelId: string): Promise<void> {
    const trimmedId = modelId.trim();
    if (!trimmedId) {
      return;
    }
    const nextIds = this.pinnedModelIds.includes(trimmedId)
      ? this.pinnedModelIds.filter(id => id !== trimmedId)
      : [...this.pinnedModelIds, trimmedId];
    await this.setPinnedModelIds(nextIds);
  }

  setModelParameter(parameterId: string, value: string): void {
    const trimmedParameterId = parameterId.trim();
    const trimmedValue = value.trim();
    if (!trimmedParameterId || !trimmedValue) {
      return;
    }

    runInAction(() => {
      const baseSelection = this.chatModel ?? this.defaultModel;
      const model = this.findModelById(baseSelection.id);
      const definition = model?.parameters?.find(parameter => parameter.id === trimmedParameterId);
      if (!definition || !definition.values.some(option => option.value === trimmedValue)) {
        return;
      }
      this.chatModel = {
        id: baseSelection.id,
        params: this.withUpdatedParameter(baseSelection.params, trimmedParameterId, trimmedValue),
      };
    });
  }

  async setDefaultModel(modelId: string): Promise<void> {
    const trimmedModelId = modelId.trim();
    if (!trimmedModelId) {
      return;
    }
    const selection = this.createInitialModelSelection(trimmedModelId);
    runInAction(() => {
      this.defaultModel = selection;
    });
    await this.root.updateGatewaySettings({ defaultModel: selection });
  }

  async setDefaultModelSelection(selection: GatewayModelSelection): Promise<void> {
    const modelId = selection.id.trim();
    if (!modelId) {
      return;
    }
    const model = this.findModelById(modelId);
    const nextSelection = sanitiseSelectionParams(selection, model);
    runInAction(() => {
      this.defaultModel = nextSelection;
      this.chatModel = null;
    });
    await this.root.updateGatewaySettings({ defaultModel: nextSelection });
  }

  async setDefaultModelParameter(parameterId: string, value: string): Promise<void> {
    const trimmedParameterId = parameterId.trim();
    const trimmedValue = value.trim();
    if (!trimmedParameterId || !trimmedValue) {
      return;
    }

    const definition = this.defaultModelInfo?.parameters?.find(parameter => parameter.id === trimmedParameterId);
    if (!definition || !definition.values.some(option => option.value === trimmedValue)) {
      return;
    }

    const nextDefaultModel: GatewayModelSelection = {
      id: this.defaultModel.id,
      params: this.withUpdatedParameter(this.defaultModel.params, trimmedParameterId, trimmedValue),
    };

    runInAction(() => {
      this.defaultModel = nextDefaultModel;
    });
    await this.root.updateGatewaySettings({ defaultModel: nextDefaultModel });
  }

  async loadAvailableModels(): Promise<void> {
    const gateway = window.electronAPI?.gateway;
    if (!gateway || this.isLoadingModels) return;
    const showLoading = this.availableModels.length === 0;
    if (showLoading) {
      runInAction(() => {
        this.isLoadingModels = true;
      });
    }

    try {
      const models = await gateway.getAvailableModels();
      if (DEBUG.logAvailableModels) {
        console.log('[ai-assistant] gateway available models', {
          count: models.length,
          models,
          summary: models.map(model => ({
            id: model.id,
            displayName: model.displayName,
            contextWindowTokens: model.contextWindowTokens,
            parameterIds: model.parameters?.map(parameter => parameter.id) ?? [],
          })),
        });
      }
      runInAction(() => {
        this.applyAvailableModels(models);
      });
    } finally {
      if (showLoading) {
        runInAction(() => {
          this.isLoadingModels = false;
        });
      }
    }
  }

  toggleReasoning(): void {
    this.showReasoning = !this.showReasoning;
    localStorageHelper.setBoolean(STORAGE_KEY_SHOW_REASONING, this.showReasoning);
  }

  setMessageContentFontSizePx(px: number): void {
    const clamped = clampMessageContentFontSize(px);
    this.messageContentFontSizePx = clamped;
    localStorageHelper.setNumber(STORAGE_KEY_MESSAGE_CONTENT_FONT_SIZE, clamped);
  }

  getDefaultParameterValue(parameter: GatewayModelParameterDefinition): string {
    return this.getParameterValue(this.defaultModel, parameter);
  }

  getChatParameterValue(parameter: GatewayModelParameterDefinition): string {
    return this.getParameterValue(this.effectiveChatModel, parameter);
  }

  setModelVariant(displayName: string): void {
    const model = this.chatModelInfo;
    const variant = model?.variants?.find(item => item.displayName === displayName);
    if (!variant) {
      return;
    }

    runInAction(() => {
      this.chatModel = {
        id: this.effectiveChatModel.id,
        params: variant.params.map(param => ({ id: param.id, value: param.value })),
      };
    });
  }

  async setDefaultModelVariant(displayName: string): Promise<void> {
    const variant = this.defaultModelInfo?.variants?.find(item => item.displayName === displayName);
    if (!variant) {
      return;
    }

    const nextDefaultModel: GatewayModelSelection = {
      id: this.defaultModel.id,
      params: variant.params.map(param => ({ id: param.id, value: param.value })),
    };

    runInAction(() => {
      this.defaultModel = nextDefaultModel;
    });
    await this.root.updateGatewaySettings({ defaultModel: nextDefaultModel });
  }

  applyModelSettings(settings: GatewaySettings): void {
    this.runMode = settings.runMode;
    this.defaultModel = this.reconcileModelSelection(settings.defaultModel);
    this.pinnedModelIds = Array.isArray(settings.pinnedModelIds)
      ? [...settings.pinnedModelIds]
      : [...ASSISTANT_DEFAULT_PINNED_MODEL_IDS];
    this.reconcilePinnedModels();
  }

  applyAvailableModels(models: GatewayModelInfo[]): void {
    this.availableModels = models;
    this.defaultModel = this.reconcileModelSelection(this.defaultModel);
    if (this.chatModel) {
      this.chatModel = this.reconcileModelSelection(this.chatModel);
    }
    this.reconcilePinnedModels();
  }

  resolveModelSelectionForGateway(): GatewayModelSelection {
    return this.reconcileModelSelection(this.effectiveChatModel);
  }

  private reconcilePinnedModels(): void {
    if (this.availableModels.length === 0) {
      return;
    }
    const nextIds = normalisePinnedModelIds(this.availableModels, this.pinnedModelIds);
    if (nextIds.join('|') !== this.pinnedModelIds.join('|')) {
      this.pinnedModelIds = nextIds;
      void this.root.updateGatewaySettings({ pinnedModelIds: nextIds });
    }
  }

  private createInitialModelSelection(modelId: string): GatewayModelSelection {
    const model = this.findModelById(modelId);
    const selection = getDefaultSelectionForModel(model) ?? { id: modelId };
    return this.reconcileModelSelection(selection);
  }

  private reconcileModelSelection(selection: GatewayModelSelection): GatewayModelSelection {
    const model = this.findModelById(selection.id);
    return mergeSelectionWithEditorDefaults(
      sanitiseSelectionParams(selection, model),
      model,
    );
  }

  private resolveContextTokensFromSelection(model: GatewayModelInfo): number | undefined {
    const contextParameter = model.parameters?.find(
      param => CONTEXT_PARAM_IDS.has(param.id.toLowerCase()),
    );
    if (!contextParameter) {
      return undefined;
    }

    const selectedValue = this.getParameterValue(this.effectiveChatModel, contextParameter);
    const selectedOption = contextParameter.values.find(option => option.value === selectedValue);
    return parseContextTokens(selectedOption?.displayName ?? selectedOption?.value ?? selectedValue);
  }

  private findModelById(modelId: string | null | undefined): GatewayModelInfo | undefined {
    if (!modelId) {
      return undefined;
    }
    return this.availableModels.find(model => model.id === modelId);
  }

  private getParameterValue(selection: GatewayModelSelection, parameter: GatewayModelParameterDefinition): string {
    const model = this.findModelById(selection.id);
    return getPresetParameterValue(selection, parameter, model);
  }

  private withUpdatedParameter(
    params: GatewayModelParameterSelection[] | undefined,
    parameterId: string,
    value: string,
  ): GatewayModelParameterSelection[] {
    const existingParams = params ?? [];
    const nextParams = existingParams.filter(param => param.id !== parameterId);
    nextParams.push({ id: parameterId, value });
    return nextParams;
  }

  private getSelectedVariantName(
    selection: GatewayModelSelection,
    model: GatewayModelInfo | undefined,
  ): string | null {
    if (!model?.variants?.length) {
      return null;
    }

    for (const variant of model.variants) {
      if (this.matchesVariant(selection, variant.params)) {
        return variant.displayName;
      }
    }

    return null;
  }

  private matchesVariant(
    selection: GatewayModelSelection,
    variantParams: GatewayModelParameterSelection[],
  ): boolean {
    const selectedParams = selection.params ?? [];
    if (selectedParams.length !== variantParams.length) {
      return false;
    }

    return variantParams.every(variantParam => (
      selectedParams.some(param => param.id === variantParam.id && param.value === variantParam.value)
    ));
  }
}
